<script setup>
import { ref, onMounted, watch } from 'vue';
import { setMeta } from '@/utils/util';

onMounted(() => {
  document.title = "颜色转换工具 - 在线颜色格式转换";
  setMeta("description", "免费在线颜色转换工具，支持 HEX、RGB、RGBA、HSL、HSLA 等格式互转。提供颜色选择器和实时预览功能。")
  setMeta("keywords", "颜色转换,hex转rgb,rgb转hex,颜色格式,颜色选择器,颜色工具,hsl转换器")
  
  // 添加结构化数据以优化SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "颜色转换工具",
    "description": "在线颜色格式转换工具，支持实时预览",
    "applicationCategory": "DeveloperApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "CNY"
    },
    "featureList": [
      "HEX 转 RGB/RGBA",
      "RGB/RGBA 转 HEX",
      "HSL/HSLA 支持",
      "颜色选择器",
      "实时颜色预览"
    ]
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.text = JSON.stringify(structuredData);
  document.head.appendChild(script);
});

const colorInput = ref('#1890ff');
const rgbaValues = ref({ r: 24, g: 144, b: 255, a: 1 });
const hslaValues = ref({ h: 209, s: 100, l: 55, a: 1 });
const colorName = ref('');

// 新增：处理颜色选择器的变化
const handleColorChange = (color) => {
  // 如果是 rgba 格式，转换为 hex
  if (color.startsWith('rgba')) {
    const matches = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/);
    if (matches) {
      const [_, r, g, b] = matches;
      colorInput.value = rgbaToHex({ r: parseInt(r), g: parseInt(g), b: parseInt(b) });
    }
  } else {
    colorInput.value = color;
  }
  updateFromHex();
};

const hexToRgba = (hex) => {
  // 移除 # 号
  hex = hex.replace('#', '');
  
  // 处理简写形式 (#RGB)
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }
  
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
    a: 1
  } : null;
};

const rgbaToHex = ({ r, g, b }) => {
  return '#' + [r, g, b].map(x => {
    const hex = Math.round(x).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

const rgbaToHsla = ({ r, g, b, a }) => {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
    a
  };
};

const hslaToRgba = ({ h, s, l, a }) => {
  s /= 100;
  l /= 100;
  const k = n => (n + h / 30) % 12;
  const a2 = s * Math.min(l, 1 - l);
  const f = n =>
    l - a2 * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return {
    r: Math.round(255 * f(0)),
    g: Math.round(255 * f(8)),
    b: Math.round(255 * f(4)),
    a
  };
};

const updateFromHex = () => {
  const rgba = hexToRgba(colorInput.value);
  if (rgba) {
    rgbaValues.value = rgba;
    hslaValues.value = rgbaToHsla(rgba);
  }
};

const updateFromRgba = () => {
  // 确保值在有效范围内
  rgbaValues.value = {
    r: Math.min(255, Math.max(0, Math.round(rgbaValues.value.r))),
    g: Math.min(255, Math.max(0, Math.round(rgbaValues.value.g))),
    b: Math.min(255, Math.max(0, Math.round(rgbaValues.value.b))),
    a: Math.min(1, Math.max(0, Number(rgbaValues.value.a.toFixed(2))))
  };
  colorInput.value = rgbaToHex(rgbaValues.value);
  hslaValues.value = rgbaToHsla(rgbaValues.value);
};

const updateFromHsla = () => {
  // 确保值在有效范围内
  hslaValues.value = {
    h: Math.min(360, Math.max(0, Math.round(hslaValues.value.h))),
    s: Math.min(100, Math.max(0, Math.round(hslaValues.value.s))),
    l: Math.min(100, Math.max(0, Math.round(hslaValues.value.l))),
    a: Math.min(1, Math.max(0, Number(hslaValues.value.a.toFixed(2))))
  };
  rgbaValues.value = hslaToRgba(hslaValues.value);
  colorInput.value = rgbaToHex(rgbaValues.value);
};

watch(colorInput, updateFromHex);
watch(rgbaValues, updateFromRgba, { deep: true });
watch(hslaValues, updateFromHsla, { deep: true });

const copy = (text) => {
  navigator.clipboard.writeText(text);
};

const reset = () => {
  colorInput.value = '#1890ff';
  updateFromHex();
};
</script>

<template>
  <div class="tool-container">
    <h1 style="margin-bottom: 20px">颜色转换工具</h1>
    
    <div class="color-preview" :style="{ backgroundColor: colorInput }"></div>
    
    <div class="converter-section">
      <div class="input-group">
        <label>HEX 颜色值：</label>
        <div class="hex-input">
          <el-input v-model="colorInput" placeholder="#000000">
            <template #prepend>#</template>
            <template #append>
              <el-button @click="copy(colorInput)">复制</el-button>
            </template>
          </el-input>
          <el-color-picker 
            v-model="colorInput" 
            :show-alpha="false"
            @change="handleColorChange">
          </el-color-picker>
        </div>
      </div>

      <div class="input-group">
        <label>RGB/RGBA：</label>
        <div class="color-inputs">
          <div class="input-with-label">
            <span class="input-label">R</span>
            <el-input v-model.number="rgbaValues.r" type="number" :min="0" :max="255" placeholder="0-255">
              <template #append>0-255</template>
            </el-input>
          </div>
          <div class="input-with-label">
            <span class="input-label">G</span>
            <el-input v-model.number="rgbaValues.g" type="number" :min="0" :max="255" placeholder="0-255">
              <template #append>0-255</template>
            </el-input>
          </div>
          <div class="input-with-label">
            <span class="input-label">B</span>
            <el-input v-model.number="rgbaValues.b" type="number" :min="0" :max="255" placeholder="0-255">
              <template #append>0-255</template>
            </el-input>
          </div>
          <div class="input-with-label">
            <span class="input-label">A</span>
            <el-input v-model.number="rgbaValues.a" type="number" :min="0" :max="1" :step="0.1" placeholder="0-1">
              <template #append>0-1</template>
            </el-input>
          </div>
        </div>
        <div class="format-preview">
          <span class="format-text">rgba({{ rgbaValues.r }}, {{ rgbaValues.g }}, {{ rgbaValues.b }}, {{ rgbaValues.a }})</span>
          <el-button @click="copy(`rgba(${rgbaValues.r}, ${rgbaValues.g}, ${rgbaValues.b}, ${rgbaValues.a})`)">
            复制
          </el-button>
        </div>
      </div>

      <div class="input-group">
        <label>HSL/HSLA：</label>
        <div class="color-inputs">
          <div class="input-with-label">
            <span class="input-label">H</span>
            <el-input v-model.number="hslaValues.h" type="number" :min="0" :max="360" placeholder="0-360">
              <template #append>0-360°</template>
            </el-input>
          </div>
          <div class="input-with-label">
            <span class="input-label">S</span>
            <el-input v-model.number="hslaValues.s" type="number" :min="0" :max="100" placeholder="0-100">
              <template #append>0-100%</template>
            </el-input>
          </div>
          <div class="input-with-label">
            <span class="input-label">L</span>
            <el-input v-model.number="hslaValues.l" type="number" :min="0" :max="100" placeholder="0-100">
              <template #append>0-100%</template>
            </el-input>
          </div>
          <div class="input-with-label">
            <span class="input-label">A</span>
            <el-input v-model.number="hslaValues.a" type="number" :min="0" :max="1" :step="0.1" placeholder="0-1">
              <template #append>0-1</template>
            </el-input>
          </div>
        </div>
        <div class="format-preview">
          <span class="format-text">hsla({{ hslaValues.h }}°, {{ hslaValues.s }}%, {{ hslaValues.l }}%, {{ hslaValues.a }})</span>
          <el-button @click="copy(`hsla(${hslaValues.h}, ${hslaValues.s}%, ${hslaValues.l}%, ${hslaValues.a})`)">
            复制
          </el-button>
        </div>
      </div>
    </div>

    <div class="btn-area">
      <el-button @click="reset">重置</el-button>
    </div>

    <div class="introduce">
      <h2>什么是颜色格式？🎨</h2>
      <p>
        在网页开发和设计中，我们使用不同的颜色格式来表示颜色。以下是主要的颜色格式：
      </p>
      
      <h3>HEX 颜色</h3>
      <p>
        十六进制颜色代码以 # 开头，后跟 6 个字符，表示 RGB 值。
        例如：#FF0000（红色）
      </p>

      <h3>RGB/RGBA 颜色</h3>
      <p>
        RGB 使用三个数字（0-255）分别表示红、绿、蓝。RGBA 增加了 Alpha 通道（0-1）表示透明度。
        例如：rgb(255, 0, 0) 或 rgba(255, 0, 0, 1)
      </p>

      <h3>HSL/HSLA 颜色</h3>
      <p>
        HSL 代表色相（0-360）、饱和度（0-100%）和亮度（0-100%）。
        HSLA 增加了 Alpha 通道表示透明度。
        例如：hsl(0, 100%, 50%) 或 hsla(0, 100%, 50%, 1)
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.tool-container {
  max-width: 800px;
  margin: 0 auto;
}

.color-preview {
  width: 100%;
  height: 120px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #dcdfe6;
  background-image: linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: v-bind('colorInput');
  }
}

.converter-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: bold;
    color: #606266;
  }
}

.hex-input {
  display: flex;
  gap: 10px;
  align-items: center;
}

.color-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
}

.input-with-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.input-label {
  font-size: 14px;
  color: #606266;
  margin-left: 4px;
}

.format-preview {
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: #f5f7fa;
  padding: 8px 12px;
  border-radius: 4px;
  margin-top: 10px;
}

.format-text {
  font-family: monospace;
  color: #606266;
  flex: 1;
  font-size: 14px;
}

.btn-area {
  display: flex;
  justify-content: center;
  margin: 30px 0;
}

.introduce {
  margin-top: 30px;
  padding: 20px;
  border-radius: 8px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;

  h2 {
    margin-bottom: 16px;
  }

  h3 {
    margin: 20px 0 10px;
    color: #2c3e50;
  }

  p {
    margin-bottom: 16px;
    line-height: 1.6;
    color: #606266;
  }
}

:deep(.el-input-group__append) {
  padding: 0 8px;
  color: #909399;
  font-size: 12px;
}

:deep(.el-input__wrapper) {
  padding-right: 0;
}

:deep(.el-input__inner) {
  text-align: center;
}
</style> 